import React, { useEffect, useRef, useState } from "react";

import {
  getSectionAnchorId,
  getSectionDisplayTitle,
  getQuoteAnchorId,
  getQuoteSummary,
} from "../../../utils/proposalDocuments/quoteOutline";
import { useProposalDocument } from "../proposalDocumentsContext";

interface OutlineCard {
  id: string;
  label: string;
  summary: string;
}

interface OutlineSection {
  id: string;
  label: string;
  isCurrent: boolean;
  cards: OutlineCard[];
}

// Sticky nav mirroring the Quote Section / Supplier Quote Card structure,
// so the whole shape of a long proposal is visible without scrolling
// through it. Highlights whichever item is currently at the top of the
// scroll viewport (scrollspy) and jumps to + expands an item on click.
const QuotesOutlineRail: React.FC = () => {
  const { payload, requestQuoteExpand } = useProposalDocument();
  const navRef = useRef<HTMLElement>(null);
  const intersectingRef = useRef<Map<string, boolean>>(new Map());
  const [activeId, setActiveId] = useState<string | null>(null);

  const outline: OutlineSection[] = payload.quote_sections.map((section, sIndex) => ({
    id: getSectionAnchorId(sIndex),
    label: getSectionDisplayTitle(section),
    isCurrent: section.current,
    cards: section.proposed.map((quote, qIndex) => ({
      id: getQuoteAnchorId(sIndex, qIndex),
      label: `Option #${(qIndex + 1).toString()}`,
      summary: getQuoteSummary(quote).join(" · "),
    })),
  }));

  const allIds = outline.flatMap((section) => [section.id, ...section.cards.map((c) => c.id)]);
  // Stable dependency for the observer effect — only rebuild when sections
  // or cards are added/removed, not on every keystroke elsewhere in the form
  const idsKey = allIds.join(",");

  useEffect(() => {
    if (allIds.length === 0) return undefined;

    // The actual scroll container is AppLayout's <main className="content">,
    // not the viewport — the observer root has to match or it never fires
    const root = navRef.current?.closest(".content") as HTMLElement | null;
    intersectingRef.current = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          intersectingRef.current.set(entry.target.id, entry.isIntersecting);
        });

        // First-in-document-order intersecting item wins; if nothing
        // currently intersects, keep the previous active item rather than
        // flashing to nothing between sections
        const firstVisible = allIds.find((id) => intersectingRef.current.get(id));
        if (firstVisible) setActiveId(firstVisible);
      },
      {
        root,
        rootMargin: "0px 0px -70% 0px",
        threshold: 0,
      },
    );

    allIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  if (outline.length === 0) return null;

  // Expanding a previously-collapsed section mounts its whole card list in
  // one commit, all default-expanded — sibling collapsing (when navigating
  // to a specific card) only takes effect a commit *later*, via each
  // sibling's own useEffect. Finding the target element the instant it
  // exists isn't enough: it can still be sitting in that transient
  // "everything expanded" layout, so scrollIntoView lands on a position
  // that then shifts once the siblings actually collapse. Instead, poll
  // the element's position frame-to-frame and only scroll once it stops
  // moving (layout has settled).
  const waitForStablePosition = (
    id: string,
    onSettled: (el: HTMLElement) => void,
    lastTop: number | null = null,
    attempt = 0,
  ) => {
    const el = document.getElementById(id);
    const giveUp = attempt >= 40; // ~0.6s at 60fps

    if (el) {
      const top = el.getBoundingClientRect().top;
      if ((lastTop !== null && Math.abs(top - lastTop) < 1) || giveUp) {
        onSettled(el);
        return;
      }
      requestAnimationFrame(() => {
        waitForStablePosition(id, onSettled, top, attempt + 1);
      });
      return;
    }

    if (giveUp) return;
    requestAnimationFrame(() => {
      waitForStablePosition(id, onSettled, lastTop, attempt + 1);
    });
  };

  // `idsToExpand` must include every ancestor collapsible, not just the
  // scroll target — a card's own id means nothing if its parent section is
  // still collapsed, since the card isn't even mounted for scrollIntoView
  // to find. `collapseSiblingsInGroup`, when navigating to a specific card,
  // collapses its sibling options within the same section so only the
  // clicked one ends up expanded.
  const handleNavigate = (
    scrollToId: string,
    idsToExpand: string[],
    collapseSiblingsInGroup?: string,
  ) => {
    requestQuoteExpand(idsToExpand, collapseSiblingsInGroup);
    waitForStablePosition(scrollToId, (el) => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <nav className="quotes-outline-rail" ref={navRef} aria-label="Quote sections outline">
      <span className="quotes-outline-rail__heading">Outline</span>
      <ul className="quotes-outline-rail__list">
        {outline.map((section) => {
          const hasActiveChild = section.cards.some((c) => c.id === activeId);
          return (
            <li key={section.id}>
              <button
                type="button"
                className={`quotes-outline-rail__item ${activeId === section.id ? "is-active" : ""} ${hasActiveChild ? "has-active-child" : ""}`}
                onClick={() => {
                  handleNavigate(section.id, [section.id]);
                }}
              >
                {section.label}
                {section.isCurrent && " (Current)"}
              </button>

              {section.cards.length > 0 && (
                <ul className="quotes-outline-rail__cards">
                  {section.cards.map((card) => (
                    <li key={card.id}>
                      <button
                        type="button"
                        className={`quotes-outline-rail__item quotes-outline-rail__item--card ${activeId === card.id ? "is-active" : ""}`}
                        onClick={() => {
                          handleNavigate(card.id, [section.id, card.id], section.id);
                        }}
                      >
                        {card.label}
                        {card.summary && (
                          <span className="quotes-outline-rail__summary">{card.summary}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default QuotesOutlineRail;
